import React, { useState } from 'react';
import { User, LogOut, Edit3, Globe, ExternalLink, Smartphone, Mail, Lock, Save, CheckCircle, ChevronRight, LayoutGrid, Eye } from 'lucide-react';
import { Client } from '../types';

interface ClientPortalViewProps {
    client: Client;
    onLogout: () => void;
    onOpenEditor: (client: Client) => void;
    onUpdateClient: (id: string, data: Partial<Client>) => void;
}

export const ClientPortalView: React.FC<ClientPortalViewProps> = ({ client, onLogout, onOpenEditor, onUpdateClient }) => {
    const [activeTab, setActiveTab] = useState<'products' | 'profile'>('products');
    
    // Profile State
    const [tempName, setTempName] = useState(client.name);
    const [tempEmail, setTempEmail] = useState(client.email || '');
    const [tempPassword, setTempPassword] = useState(client.password || '');
    const [isSaving, setIsSaving] = useState(false);

    const handleSaveProfile = () => {
        setIsSaving(true);
        onUpdateClient(client.id, {
            name: tempName,
            email: tempEmail,
            password: tempPassword
        });
        setTimeout(() => setIsSaving(false), 1000);
    };

    return (
        <div className="min-h-screen bg-zinc-50 font-sans text-slate-900 pb-24">
            {/* Header Mobile Style */}
            <div className="bg-white border-b border-zinc-200 px-6 py-6 sticky top-0 z-30 shadow-sm">
                <div className="flex justify-between items-center max-w-lg mx-auto">
                    <div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold mb-1">Bem-vindo,</p>
                        <h1 className="text-xl font-black text-slate-900 truncate max-w-[200px] uppercase tracking-tighter">{client.name}</h1>
                    </div>
                    <div className="w-10 h-10 bg-zinc-100 rounded-sm border border-zinc-200 flex items-center justify-center text-slate-900 font-black">
                        {client.name.substring(0, 1)}
                    </div>
                </div>
            </div>

            <div className="max-w-lg mx-auto p-6 animate-fade-in">
                
                {/* --- TAB: PRODUTOS (HOME) --- */}
                {activeTab === 'products' && (
                    <div className="space-y-6">
                        <div className="bg-slate-900 rounded-sm p-6 text-white shadow-xl relative overflow-hidden">
                             <div className="absolute top-0 right-0 p-4 opacity-10">
                                <LayoutGrid size={120} />
                             </div>
                             <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-slate-900 via-orange-700 to-slate-900"></div>
                             <p className="text-slate-400 text-[10px] uppercase tracking-widest font-bold mb-2">Seus Produtos</p>
                             <h2 className="text-2xl font-black mb-1 uppercase tracking-tight">1 Site Ativo</h2>
                             <p className="text-xs text-slate-400 font-mono">Gerenciamento de presença digital.</p>
                        </div>

                        {/* Lista de Produtos (No caso, o site dele) */}
                        <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden shadow-sm">
                            {/* Card do Site */}
                            <div className="p-5 border-b border-zinc-100">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 bg-zinc-50 rounded-sm border border-zinc-200 flex items-center justify-center text-slate-400">
                                            <Globe size={24} />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-slate-900 text-lg uppercase tracking-tight">{client.name}</h3>
                                            <a href={`?site=${client.slug || client.id}`} target="_blank" className="text-[10px] text-orange-700 font-bold uppercase tracking-widest flex items-center gap-1 hover:underline">
                                                {client.subdomain || 'Ver online'} <ExternalLink size={10} />
                                            </a>
                                        </div>
                                    </div>
                                    <span className="bg-orange-50 text-orange-700 px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-widest border border-orange-200">
                                        Online
                                    </span>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <button 
                                        onClick={() => onOpenEditor(client)}
                                        className="flex flex-col items-center justify-center bg-orange-700 hover:bg-orange-600 active:scale-95 transition-all text-white p-4 rounded-sm shadow-md"
                                    >
                                        <Edit3 size={24} className="mb-2" />
                                        <span className="text-[10px] font-bold uppercase tracking-widest">Editar Site</span>
                                    </button>
                                    
                                    <a 
                                        href={`?site=${client.slug || client.id}`} 
                                        target="_blank"
                                        className="flex flex-col items-center justify-center bg-white border border-zinc-200 hover:bg-zinc-50 active:scale-95 transition-all text-slate-700 p-4 rounded-sm"
                                    >
                                        <Eye size={24} className="mb-2 text-slate-400" />
                                        <span className="text-[10px] font-bold uppercase tracking-widest">Ver Site</span>
                                    </a>
                                </div>
                            </div>
                            
                            {/* Estatísticas */}
                            <div className="bg-zinc-50 p-4 flex justify-between text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                <div className="text-center flex-1 border-r border-zinc-200">
                                    <strong className="block text-slate-900 text-sm font-mono">{124}</strong>
                                    Visitas / Mês
                                </div>
                                <div className="text-center flex-1">
                                    <strong className="block text-slate-900 text-sm font-mono">{12}</strong>
                                    Cliques Zap
                                </div>
                            </div>
                        </div>

                        {/* Banner de Suporte */}
                        <div className="border border-dashed border-zinc-300 rounded-sm p-6 text-center bg-zinc-50/50">
                            <Smartphone size={32} className="mx-auto text-slate-300 mb-2" />
                            <h3 className="text-slate-900 font-bold mb-1 uppercase text-sm tracking-tight">Quer um App Android?</h3>
                            <p className="text-[10px] text-slate-500 mb-4 font-mono">Transforme seu site em um aplicativo nativo.</p>
                            <button className="text-orange-700 text-[10px] font-bold uppercase tracking-widest border border-orange-200 px-6 py-2 rounded-sm hover:bg-orange-50 bg-white transition-colors">
                                Falar com Suporte
                            </button>
                        </div>
                    </div>
                )}

                {/* --- TAB: PERFIL --- */}
                {activeTab === 'profile' && (
                    <div className="space-y-6">
                        <h2 className="text-xl font-black text-slate-900 uppercase tracking-tighter">Meus Dados</h2>
                        
                        <div className="bg-white border border-zinc-200 rounded-sm p-6 space-y-4 shadow-sm">
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Nome da Empresa</label>
                                <div className="flex items-center gap-3 bg-zinc-50 border border-zinc-200 rounded-sm px-4 py-3">
                                    <Smartphone size={18} className="text-slate-400" />
                                    <input 
                                        type="text" 
                                        value={tempName}
                                        onChange={(e) => setTempName(e.target.value)}
                                        className="bg-transparent w-full text-slate-900 font-bold focus:outline-none text-sm"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">E-mail de Acesso</label>
                                <div className="flex items-center gap-3 bg-zinc-50 border border-zinc-200 rounded-sm px-4 py-3">
                                    <Mail size={18} className="text-slate-400" />
                                    <input 
                                        type="email" 
                                        value={tempEmail}
                                        onChange={(e) => setTempEmail(e.target.value)}
                                        className="bg-transparent w-full text-slate-900 font-mono focus:outline-none text-sm"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Alterar Senha</label>
                                <div className="flex items-center gap-3 bg-zinc-50 border border-zinc-200 rounded-sm px-4 py-3">
                                    <Lock size={18} className="text-slate-400" />
                                    <input 
                                        type="text" 
                                        value={tempPassword}
                                        onChange={(e) => setTempPassword(e.target.value)}
                                        className="bg-transparent w-full text-slate-900 font-mono focus:outline-none text-sm"
                                        placeholder="Nova senha"
                                    />
                                </div>
                            </div>
                            
                            <button 
                                onClick={handleSaveProfile}
                                className={`w-full py-4 rounded-sm font-bold text-[10px] uppercase tracking-widest text-white flex items-center justify-center gap-2 transition-all shadow-lg ${
                                    isSaving ? 'bg-green-600' : 'bg-slate-900 hover:bg-slate-800'
                                }`}
                            >
                                {isSaving ? <CheckCircle size={18} /> : <Save size={18} />}
                                {isSaving ? 'Salvo com sucesso' : 'Salvar Alterações'}
                            </button>
                        </div>

                        <button 
                            onClick={onLogout}
                            className="w-full py-4 border border-zinc-200 text-slate-400 bg-white rounded-sm font-bold text-[10px] uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-all"
                        >
                            <LogOut size={18} /> Sair da Conta
                        </button>
                    </div>
                )}

            </div>

            {/* --- BOTTOM NAVIGATION (MOBILE APP STYLE) --- */}
            <div className="fixed bottom-0 left-0 w-full bg-white border-t border-zinc-200 pb-safe z-40 shadow-[0_-5px_20px_rgba(0,0,0,0.05)]">
                <div className="flex justify-around items-center max-w-lg mx-auto">
                    <button 
                        onClick={() => setActiveTab('products')}
                        className={`flex flex-col items-center gap-1 p-3 flex-1 transition-colors ${
                            activeTab === 'products' ? 'text-orange-700' : 'text-slate-400 hover:text-slate-600'
                        }`}
                    >
                        <LayoutGrid size={24} />
                        <span className="text-[8px] font-bold uppercase tracking-widest">Início</span>
                    </button>
                    
                    <button 
                        onClick={() => setActiveTab('profile')}
                        className={`flex flex-col items-center gap-1 p-3 flex-1 transition-colors ${
                            activeTab === 'profile' ? 'text-orange-700' : 'text-slate-400 hover:text-slate-600'
                        }`}
                    >
                        <User size={24} />
                        <span className="text-[8px] font-bold uppercase tracking-widest">Perfil</span>
                    </button>
                </div>
            </div>
        </div>
    );
};